import {
    BaseFeatureArgs,
    InitialQueryState,
    ProjectedEntity,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { FeatureQueryHookSignature, useFeatureQuery } from './index';

export const useProjectedFeatureQuery = <
    TEntity,
    Args extends BaseFeatureArgs,
    K extends keyof TEntity,
>(
    initialArgs: InitialQueryState,
    useRTKHook: FeatureQueryHookSignature<TEntity, Args>,
    featureArgs: SkipOrArgs<Args>,
    requestedFields: K[],
    options?: QueryManagerOptions,
) => {
    const projectedArgs = useMemo(() => {
        if (featureArgs === skipToken) return skipToken;

        return {
            ...featureArgs,
            fields: requestedFields as string[],
        };
    }, [featureArgs, requestedFields]);

    const { data, ...queryResult } = useFeatureQuery<TEntity, Args>(
        initialArgs,
        useRTKHook,
        projectedArgs,
        options,
    );

    return {
        data: data as ProjectedEntity<TEntity, K>[] | undefined,
        ...queryResult,
    };
};
